import React, { useEffect, useState } from "react";
import { Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import StatusPill from "@core/components/StatusPill";
import { ErrorResponse } from "@core/types/errorResponse";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { ASSIGN_MOVE_DOWN_LABEL, ASSIGN_MOVE_UP_LABEL } from "@core/components/assign/AssignAssignedRow";
import { setItemOrder, sortByListOrder } from "@core/components/assign/assignListOrder";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useFinanceDeductibleAction } from "@features/admin/finance/deductible/hooks/useFinanceDeductibleAction";
import { fetchAllFinanceDeductiblesAsync } from "@features/admin/finance/deductible/services/financeDeductibleSvc";
import { FinanceDeductible } from "@features/admin/finance/deductible/types/financeDeductible";

interface ReorderFinanceDeductiblesModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess?: () => void | Promise<void>;
}

const ReorderFinanceDeductiblesModal: React.FC<ReorderFinanceDeductiblesModalProps> = ({ show, onHide, onSuccess }) => {
    const nationId = useScopeId() ?? 0;
    const [items, setItems] = useState<FinanceDeductible[]>([]);
    const [loading, setLoading] = useState(false);
    const showError = useApiError();
    const { busy, doReorder } = useFinanceDeductibleAction();

    useEffect(() => {
        if (!show) return;
        let cancelled = false;
        setLoading(true);
        fetchAllFinanceDeductiblesAsync(nationId)
            .then((all) => {
                if (cancelled) return;
                setItems(sortByListOrder(all).map((d, i) => ({ ...d, list_order: i + 1 })));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [show, nationId]);

    const move = (id: number, newOrder: number) =>
        setItems((prev) => setItemOrder(prev, id, newOrder, (d) => d.deductible_id));

    const handleSave = async () => {
        try {
            const response = await doReorder(nationId, sortByListOrder(items).map((d) => d.deductible_id));
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "");
                onHide();
                await onSuccess?.();
            } else {
                showError(response.getError() as ErrorResponse);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    const sorted = sortByListOrder(items);

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={!busy} centered data-testid="reorder-finance-deductibles-modal">
            <Modal.Header closeButton={!busy}>
                <Modal.Title className="fw-semibold">Reorder Deductibles</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
                <p className="text-muted small">
                    This order is used for deductible rows in category and allocation rule forms and in finance reports.
                </p>
                {loading ? (
                    <div className="d-flex align-items-center small text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />Loading deductibles…
                    </div>
                ) : (
                    <ListGroup data-testid="reorder-deductible-list">
                        {sorted.map((d) => (
                            <ListGroup.Item key={d.deductible_id} className="py-2 px-3" data-testid={`reorder-deductible-${d.deductible_id}`}>
                                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-semibold">{d.name}</span>
                                        {!d.is_active && <StatusPill active={false} />}
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <Form.Label className="small mb-0 text-nowrap" htmlFor={`reorder-order-${d.deductible_id}`}>Order:</Form.Label>
                                        <Form.Control
                                            id={`reorder-order-${d.deductible_id}`}
                                            type="number"
                                            size="sm"
                                            min={1}
                                            max={sorted.length}
                                            value={d.list_order}
                                            onChange={(e) => move(d.deductible_id, Math.max(1, Number(e.target.value) || 1))}
                                            style={{ width: 70 }}
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-secondary"
                                            className="assign-icon-btn"
                                            disabled={d.list_order <= 1}
                                            onClick={() => move(d.deductible_id, d.list_order - 1)}
                                            title={ASSIGN_MOVE_UP_LABEL}
                                            aria-label={`${ASSIGN_MOVE_UP_LABEL} ${d.name}`}
                                        >
                                            <FiArrowUp aria-hidden />
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-secondary"
                                            className="assign-icon-btn"
                                            disabled={d.list_order >= sorted.length}
                                            onClick={() => move(d.deductible_id, d.list_order + 1)}
                                            title={ASSIGN_MOVE_DOWN_LABEL}
                                            aria-label={`${ASSIGN_MOVE_DOWN_LABEL} ${d.name}`}
                                        >
                                            <FiArrowDown aria-hidden />
                                        </Button>
                                    </div>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={busy || loading || items.length === 0} aria-busy={busy} data-testid="reorder-deductibles-save-btn">
                    {busy ? (<><Spinner animation="border" size="sm" className="me-2" />Saving…</>) : "Save Order"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReorderFinanceDeductiblesModal;
